import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GridColumnVisibilityModel, GridRowParams } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { getApiErrorMessage } from '../../../../shared/api/getApiErrorMessage';
import { DIAM_GRID_PAGE_SIZE_OPTIONS } from '../../diamGridUtils';
import { useDiamGridPagination } from '../../hooks/useDiamGridPagination';
import { DiamDataGrid } from '../DiamDataGrid/DiamDataGrid';
import { DataGridContainer } from '../DiamDataGrid/styles';
import { buildIdentityColumns } from './columns';
import { mapApiIdentity } from './mapApiIdentity';
import { Identity, IdentityActionHandlers } from './types';
import { useIdentities } from './useIdentities';

const COLUMN_VISIBILITY: GridColumnVisibilityModel = {
  createdAt: false,
};

export function IdentitiesGrid({ onAssign }: IdentityActionHandlers) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const {
    pageIndex,
    pageSize,
    paginationModel,
    filterModel,
    sortModel,
    handleServerFilterModelChange,
    handlePagination,
    handleSortModelChange,
  } = useDiamGridPagination();

  const identitiesQuery = useIdentities({
    page: pageIndex,
    pageSize,
    sortModel,
    filterModel,
  });

  const columns = useMemo(() => buildIdentityColumns({ onAssign }), [onAssign]);

  const rows = useMemo(
    () =>
      (identitiesQuery.data?.elements ?? []).map(item => mapApiIdentity(item)),
    [identitiesQuery.data],
  );

  useEffect(() => {
    if (identitiesQuery.isError) {
      enqueueSnackbar(
        getApiErrorMessage(identitiesQuery.error, 'Failed to load identities'),
        { variant: 'error' },
      );
    }
  }, [enqueueSnackbar, identitiesQuery.error, identitiesQuery.isError]);

  function handleRowClick(params: GridRowParams<Identity>) {
    navigate(`/diam/identities/${params.row.id}`);
  }

  return (
    <DataGridContainer elevation={1}>
      <DiamDataGrid
        rows={rows}
        columns={columns}
        loading={identitiesQuery.isPending || identitiesQuery.isFetching}
        columnVisibilityModel={COLUMN_VISIBILITY}
        disableRowSelectionOnClick
        getRowClassName={() => 'clickable'}
        onRowClick={handleRowClick}
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={handleServerFilterModelChange}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePagination}
        rowCount={identitiesQuery.data?.total ?? 0}
        pageSizeOptions={[...DIAM_GRID_PAGE_SIZE_OPTIONS]}
        searchPlaceholder="Search identities"
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
      />
    </DataGridContainer>
  );
}
